import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { settingsApi } from '../api';

import { DateFormat } from '../utils/dateFormat';

interface SocialMediaLinks {
  facebook?: string;
  twitter?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
  whatsapp?: string;
  tiktok?: string;
}

interface SettingsContextType {
  platformName: string;
  platformDescription: string;
  systemLogo: string | null;
  systemLogoDark: string | null;
  systemFavicon: string | null;
  showSystemName: boolean;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  heroYouTubeUrl: string | null;
  heroVideoThumbnail: string | null;
  authenticationHubImage: string | null;
  radioStreamUrl: string | null;
  dateFormat: DateFormat;
  socialMediaLinks: SocialMediaLinks;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

const defaultSettingsValue: SettingsContextType = {
  platformName: 'Sisi ni Tanzania',
  platformDescription: 'Empowering Youth Voices',
  systemLogo: null,
  systemLogoDark: null,
  systemFavicon: null,
  showSystemName: true,
  primaryColor: '#226D12',
  secondaryColor: '#2563EB',
  tertiaryColor: '#10B981',
  heroYouTubeUrl: null,
  heroVideoThumbnail: null,
  authenticationHubImage: null,
  radioStreamUrl: null,
  dateFormat: 'DD/MM/YYYY',
  socialMediaLinks: {},
  loading: false,
  refreshSettings: async () => {},
};

const SettingsContext = createContext<SettingsContextType>(defaultSettingsValue);

export const useSettings = (): SettingsContextType => {
  return useContext(SettingsContext);
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  // Load colors from localStorage immediately to prevent FOUC
  const getInitialPrimaryColor = (): string => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('vijana-primary-color');
      if (saved) return saved;
    }
    return '#226D12'; // Default to brand color instead of blue
  };

  const getInitialSecondaryColor = (): string => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('vijana-secondary-color');
      if (saved) return saved;
    }
    return '#2563EB'; // Default secondary color
  };

  const getInitialTertiaryColor = (): string => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('vijana-tertiary-color');
      if (saved) return saved;
    }
    return '#10B981'; // Default tertiary color
  };

  const [platformName, setPlatformName] = useState<string>('Sisi ni Tanzania');
  const [platformDescription, setPlatformDescription] = useState<string>('Empowering Youth Voices');
  const [systemLogo, setSystemLogo] = useState<string | null>(null);
  const [systemLogoDark, setSystemLogoDark] = useState<string | null>(null);
  const [systemFavicon, setSystemFavicon] = useState<string | null>(null);
  const [showSystemName, setShowSystemName] = useState<boolean>(true);
  const [primaryColor, setPrimaryColor] = useState<string>(getInitialPrimaryColor());
  const [secondaryColor, setSecondaryColor] = useState<string>(getInitialSecondaryColor());
  const [tertiaryColor, setTertiaryColor] = useState<string>(getInitialTertiaryColor());
  const [heroYouTubeUrl, setHeroYouTubeUrl] = useState<string | null>(null);
  const [heroVideoThumbnail, setHeroVideoThumbnail] = useState<string | null>(null);
  const [authenticationHubImage, setAuthenticationHubImage] = useState<string | null>(null);
  const [radioStreamUrl, setRadioStreamUrl] = useState<string | null>(null);
  const [dateFormat, setDateFormat] = useState<DateFormat>('DD/MM/YYYY');
  const [socialMediaLinks, setSocialMediaLinks] = useState<SocialMediaLinks>({});
  const [loading, setLoading] = useState(true);

  const refreshSettings = async () => {
    try {
      // Use public endpoints for settings that should be accessible without authentication
      const [
        nameSetting,
        descSetting,
        logoSetting,
        logoDarkSetting,
        faviconSetting,
        showSystemNameSetting,
        primaryColorSetting,
        secondaryColorSetting,
        tertiaryColorSetting,
        heroYouTubeUrlSetting,
        heroVideoThumbnailSetting,
        authHubImageSetting,
        radioStreamUrlSetting,
        dateFormatSetting,
        socialMediaSetting,
      ] = await Promise.all([
        settingsApi.getPublicSetting('platform_name').catch(() => null),
        settingsApi.getPublicSetting('platform_description').catch(() => null),
        settingsApi.getPublicSetting('system_logo').catch(() => null),
        settingsApi.getPublicSetting('system_logo_dark').catch((err) => {
          // Silently handle 403 or 404 for dark logo (it's optional)
          return null;
        }),
        settingsApi.getPublicSetting('system_favicon').catch(() => null),
        settingsApi.getPublicSetting('show_system_name').catch(() => null),
        settingsApi.getPublicSetting('primary_color').catch(() => null),
        settingsApi.getPublicSetting('secondary_color').catch(() => null),
        settingsApi.getPublicSetting('tertiary_color').catch(() => null),
        settingsApi.getPublicSetting('hero_youtube_url').catch(() => null),
        settingsApi.getPublicSetting('hero_video_thumbnail').catch(() => null),
        settingsApi.getPublicSetting('authentication_hub_image').catch(() => null),
        settingsApi.getPublicSetting('radio_stream_url').catch((err) => {
          // If not available publicly, return null and use default
          return null;
        }),
        settingsApi.getPublicSetting('date_format').catch(() => null),
        settingsApi.getPublicSetting('social_media_links').catch(() => null),
      ]);

      if (nameSetting?.setting?.value) {
        setPlatformName(nameSetting.setting.value);
      }
      if (descSetting?.setting?.value) {
        setPlatformDescription(descSetting.setting.value);
      }
      // Helper function to get base URL
      const getBaseUrl = () => {
        const apiBaseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '';
        if (apiBaseUrl) {
          // Remove /api/v1 or /api/v1/ from the end
          const base = apiBaseUrl.replace(/\/api\/v1\/?$/, '');
          if (base) return base;
        }
        // Fallback to current origin or localhost
        if (typeof window !== 'undefined') {
          return window.location.origin;
        }
        return 'http://localhost:8000';
      };
      
      const baseUrl = getBaseUrl();
      
      // Helper function to construct full URL from storage path
      const constructImageUrl = (url: string): string => {
        if (!url || url.trim() === '') {
          return '';
        }
        let trimmedUrl = url.trim();
        
        // Clean up any duplicate /backend/ segments first
        trimmedUrl = trimmedUrl.replace(/(\/backend\/)+backend\//g, '/backend/');
        trimmedUrl = trimmedUrl.replace(/(\/backend){2,}/g, '/backend');
        
        // If already a full URL, return as-is (after cleanup)
        if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
          return trimmedUrl;
        }
        
        // Remove any leading slashes, 'backend/', and normalize 'storage/' prefix
        let normalizedPath = trimmedUrl.replace(/^\/+/, '').replace(/^backend\//, '');
        
        // Remove duplicate 'storage/' prefixes (handle cases like 'storage/storage/media/...')
        normalizedPath = normalizedPath.replace(/^(storage\/)+/, 'storage/');
        
        // If path doesn't start with 'storage/', add it
        // But first check if it starts with 'media/' which should become 'storage/media/'
        if (!normalizedPath.startsWith('storage/')) {
          if (normalizedPath.startsWith('media/')) {
            normalizedPath = 'storage/' + normalizedPath;
          } else {
            // For any other path, add 'storage/' prefix
            normalizedPath = 'storage/' + normalizedPath;
          }
        }
        
        // Determine storage path based on environment
        // In development (localhost), storage is at /storage
        // In production (with /backend subdirectory), storage is at /backend/storage
        const isDevelopment = import.meta.env.DEV || 
                             baseUrl.includes('localhost') || 
                             baseUrl.includes('127.0.0.1');
        
        // Check if baseUrl already includes /backend
        const hasBackendInBaseUrl = baseUrl.includes('/backend');
        
        let storageBase: string;
        if (hasBackendInBaseUrl) {
          // Base URL already includes /backend, so just append /storage
          storageBase = baseUrl.replace(/\/+$/, '') + '/storage';
        } else if (isDevelopment) {
          // Development: just /storage
          storageBase = baseUrl.replace(/\/+$/, '') + '/storage';
        } else {
          // Production: /backend/storage
          storageBase = baseUrl.replace(/\/+$/, '') + '/backend/storage';
        }
        
        // Construct full URL (ensure single slash between base and path)
        // Remove leading slash from normalizedPath to avoid double slashes
        const pathWithoutLeadingSlash = normalizedPath.replace(/^\/+/, '');
        const fullUrl = storageBase + '/' + pathWithoutLeadingSlash;
        
        // Final cleanup of any duplicate /backend/ or /storage/ segments
        let cleanedUrl = fullUrl.replace(/(\/backend\/)+backend\//g, '/backend/');
        cleanedUrl = cleanedUrl.replace(/(\/backend){2,}/g, '/backend');
        cleanedUrl = cleanedUrl.replace(/(\/storage\/)+storage\//g, '/storage/');
        cleanedUrl = cleanedUrl.replace(/(\/storage){2,}/g, '/storage');
        
        return cleanedUrl;
      };
      
      // Helper function for URLs that might not need /storage/ prefix (like YouTube thumbnails)
      const constructFullUrl = (url: string): string => {
        if (!url || url.trim() === '') {
          return '';
        }
        const trimmedUrl = url.trim();
        
        // If already a full URL, return as-is
        if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
          return trimmedUrl;
        }
        
        // For relative paths, construct full URL
        const normalizedPath = trimmedUrl.replace(/^\/+/, '');
        return `${baseUrl}/${normalizedPath}`;
      };
      
      if (logoSetting?.setting?.value && logoSetting.setting.value.trim() !== '') {
        const logoUrl = logoSetting.setting.value.trim();
        const fullLogoUrl = constructImageUrl(logoUrl);
        // Ensure we have a valid URL before setting
        if (fullLogoUrl && fullLogoUrl.trim() !== '' && (fullLogoUrl.startsWith('http://') || fullLogoUrl.startsWith('https://'))) {
          setSystemLogo(fullLogoUrl);
        } else {
          if (import.meta.env.DEV) {
            console.warn('Logo URL construction failed - invalid URL', { 
              original: logoUrl, 
              constructed: fullLogoUrl,
              baseUrl: baseUrl
            });
          }
          setSystemLogo(null);
        }
      } else {
        setSystemLogo(null);
      }
      if (logoDarkSetting?.setting?.value && logoDarkSetting.setting.value.trim() !== '') {
        const logoDarkUrl = logoDarkSetting.setting.value.trim();
        const fullLogoDarkUrl = constructImageUrl(logoDarkUrl);
        // Ensure we have a valid URL before setting
        if (fullLogoDarkUrl && fullLogoDarkUrl.trim() !== '' && (fullLogoDarkUrl.startsWith('http://') || fullLogoDarkUrl.startsWith('https://'))) {
          setSystemLogoDark(fullLogoDarkUrl);
        } else {
          setSystemLogoDark(null);
        }
      } else {
        setSystemLogoDark(null);
      }
      if (faviconSetting?.setting?.value) {
        const faviconUrl = faviconSetting.setting.value;
        // Use constructImageUrl helper for consistent URL construction (handles storage paths)
        const fullFaviconUrl = constructImageUrl(faviconUrl);
        
        if (fullFaviconUrl && fullFaviconUrl.trim() !== '') {
          setSystemFavicon(fullFaviconUrl);
          
          // Update favicon in document
          const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
          if (link) {
            link.href = fullFaviconUrl;
          } else {
            const newLink = document.createElement('link');
            newLink.rel = 'icon';
            newLink.href = fullFaviconUrl;
            document.head.appendChild(newLink);
          }
        }
      }
      if (showSystemNameSetting?.setting?.value !== undefined) {
        setShowSystemName(showSystemNameSetting.setting.value === '1' || showSystemNameSetting.setting.value === 'true');
      }
      if (primaryColorSetting?.setting?.value) {
        setPrimaryColor(primaryColorSetting.setting.value);
      }
      if (secondaryColorSetting?.setting?.value) {
        setSecondaryColor(secondaryColorSetting.setting.value);
      }
      if (tertiaryColorSetting?.setting?.value) {
        setTertiaryColor(tertiaryColorSetting.setting.value);
      }
      if (heroYouTubeUrlSetting?.setting?.value) {
        setHeroYouTubeUrl(heroYouTubeUrlSetting.setting.value);
      } else {
        setHeroYouTubeUrl(null);
      }
      // Always set thumbnail - use null if empty/undefined to fall back to YouTube thumbnail
      if (heroVideoThumbnailSetting?.setting?.value && heroVideoThumbnailSetting.setting.value.trim() !== '') {
        const thumbnailUrl = heroVideoThumbnailSetting.setting.value.trim();
        // Use constructImageUrl helper for consistent URL construction (storage paths)
        const fullThumbnailUrl = constructImageUrl(thumbnailUrl);
        if (fullThumbnailUrl && fullThumbnailUrl.trim() !== '') {
          setHeroVideoThumbnail(fullThumbnailUrl);
        } else {
          setHeroVideoThumbnail(null);
        }
      } else {
        setHeroVideoThumbnail(null);
      }
      if (authHubImageSetting?.setting?.value && authHubImageSetting.setting.value.trim() !== '') {
        const authHubImageUrl = authHubImageSetting.setting.value.trim();
        // Use constructImageUrl helper for consistent URL construction (storage paths)
        const fullAuthHubImageUrl = constructImageUrl(authHubImageUrl);
        if (fullAuthHubImageUrl && fullAuthHubImageUrl.trim() !== '') {
          setAuthenticationHubImage(fullAuthHubImageUrl);
        } else {
          setAuthenticationHubImage(null);
        }
      } else {
        setAuthenticationHubImage(null);
      }
      if (radioStreamUrlSetting?.setting?.value && radioStreamUrlSetting.setting.value.trim() !== '') {
        setRadioStreamUrl(radioStreamUrlSetting.setting.value);
        if (import.meta.env.DEV) {
        }
      } else {
        // No default - leave as null if not configured
        setRadioStreamUrl(null);
        if (import.meta.env.DEV) {
          console.warn('Radio stream URL not found or empty:', {
            hasSetting: !!radioStreamUrlSetting,
            hasValue: !!(radioStreamUrlSetting?.setting?.value),
            value: radioStreamUrlSetting?.setting?.value
          });
        }
      }
      if (dateFormatSetting?.setting?.value) {
        const format = dateFormatSetting.setting.value as DateFormat;
        if (['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].includes(format)) {
          setDateFormat(format);
        }
      }
      if (socialMediaSetting?.setting?.value) {
        try {
          const parsedLinks = JSON.parse(socialMediaSetting.setting.value);
          setSocialMediaLinks({
            facebook: parsedLinks.facebook || '',
            twitter: parsedLinks.twitter || '',
            instagram: parsedLinks.instagram || '',
            linkedin: parsedLinks.linkedin || '',
            youtube: parsedLinks.youtube || '',
            whatsapp: parsedLinks.whatsapp || '',
            tiktok: parsedLinks.tiktok || '',
          });
        } catch (e) {
          console.error('Failed to parse social media links:', e);
          setSocialMediaLinks({});
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
      // Dispatch custom event when settings are loaded
      window.dispatchEvent(new CustomEvent('settings-loaded'));
    }
  };

  // Apply colors when they change (including on mount)
  useEffect(() => {
    const root = document.documentElement;
    const getContrastColor = (hex: string) => {
      if (!hex || !hex.startsWith('#')) return '#ffffff';
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128 ? '#000000' : '#ffffff';
    };
    
    root.style.setProperty('--color-primary', primaryColor);
    root.style.setProperty('--color-secondary', secondaryColor);
    root.style.setProperty('--color-tertiary', tertiaryColor);
    root.style.setProperty('--color-primary-foreground', getContrastColor(primaryColor));
    root.style.setProperty('--color-secondary-foreground', getContrastColor(secondaryColor));
    root.style.setProperty('--color-tertiary-foreground', getContrastColor(tertiaryColor));
    
    // Persist to localStorage for next page load
    localStorage.setItem('vijana-primary-color', primaryColor);
    localStorage.setItem('vijana-secondary-color', secondaryColor);
    localStorage.setItem('vijana-tertiary-color', tertiaryColor);
    
    // Update theme-color meta tag
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', primaryColor);
    } else {
      // Create meta tag if it doesn't exist
      const newMeta = document.createElement('meta');
      newMeta.name = 'theme-color';
      newMeta.content = primaryColor;
      document.head.appendChild(newMeta);
    }
  }, [primaryColor, secondaryColor, tertiaryColor]); // Run when colors change

  useEffect(() => {
    refreshSettings();
  }, []);

  return (
    <SettingsContext.Provider
      value={{
        platformName,
        platformDescription,
        systemLogo,
        systemLogoDark,
        systemFavicon,
        showSystemName,
        primaryColor,
        secondaryColor,
        tertiaryColor,
        heroYouTubeUrl,
        heroVideoThumbnail,
        authenticationHubImage,
        radioStreamUrl,
        dateFormat,
        socialMediaLinks,
        loading,
        refreshSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

